import clsx from 'clsx';
import { getImageProps } from 'next/image';
import ReactDOM from 'react-dom';
import type { BaseImageStrapiType } from '@/strapi-types';
import { getBaseImageSize } from './util/getBaseImageSize';
import { getImageUrl } from './util/getImageUrl';
import { RetryingImage } from './RetryingImage';

const aspectRatioClasses: Record<string, string> = {
  'sixteen by nine': 'sm:aspect-video',
  'four by three': 'sm:aspect-[4/3]',
  'one by one': 'sm:aspect-square',
};

// The mobile Image is `block lg:hidden`, the desktop one `hidden lg:block` —
// these media queries must match Tailwind's default `lg` breakpoint.
const MOBILE_MEDIA = '(max-width: 1023.98px)';
const DESKTOP_MEDIA = '(min-width: 1024px)';

/**
 * Emit a media-scoped `<link rel="preload" as="image">` into <head> so the
 * browser's preload scanner starts the LCP image fetch early, but only for the
 * breakpoint variant that is actually visible.
 *
 * Uses ReactDOM.preload rather than a rendered <link>: React hoists a rendered
 * <link> to <head> but strips its `media` attribute, so both variants would be
 * preloaded on every viewport. ReactDOM.preload preserves `media` (and, unlike
 * an in-DOM <link>, lands in <head> instead of deep in the body where the
 * preload scanner finds it too late to help). getImageProps guarantees the
 * preloaded URL/srcset match exactly what the corresponding <Image> requests.
 */
function preloadScopedImage({
  media,
  src,
  width,
  height,
  quality,
  sizes,
}: {
  media: string;
  src: string;
  width: number;
  height: number;
  quality?: number;
  sizes?: string;
}) {
  const { props } = getImageProps({
    alt: '',
    src,
    width,
    height,
    quality,
    sizes,
  });

  ReactDOM.preload(props.src, {
    as: 'image',
    imageSrcSet: props.srcSet,
    imageSizes: props.sizes,
    media,
    fetchPriority: 'high',
  });
}

export function BaseImage({
  image,
  mobileImage,
  alt,
  classNameImage,
  objectFit,
  aspectRatio,
  squareMobile,
  ariaLabel,
  caption,
  prerender,
  className,
  width,
  height,
  overrideWidth,
  overrideHeight,
  quality = 75,
  sizes,
  preloadMedia,
}: BaseImageStrapiType) {
  const imageAlt = alt || image?.caption || image?.name || 'smzh-image';
  const aspectRatioClass = aspectRatio
    ? aspectRatioClasses[aspectRatio]
    : undefined;

  const imgClasses = clsx(classNameImage, aspectRatioClass, {
    [`object-${objectFit}`]: objectFit,
    'max-[639px]:aspect-square': squareMobile,
  });

  const mobileImageSize = getBaseImageSize({
    image: mobileImage,
    width,
    height,
    overrideWidth,
    overrideHeight,
  });
  const imageSize = getBaseImageSize({
    image,
    width,
    height,
    overrideWidth,
    overrideHeight,
  });

  const hasMobileVariant = Boolean(mobileImage?.url);
  // With a mobile/desktop pair (or an explicit media scope) the preload is
  // emitted as a media-scoped <link> instead of next/image's own preload, so
  // only the variant that is visible at the current viewport gets fetched.
  const useScopedPreload = Boolean(
    prerender && (hasMobileVariant || preloadMedia),
  );
  // Above-the-fold images (prerender) must load eagerly, otherwise next/image
  // lazy-loads them, they end up being the LCP element, and Next warns.
  // - Scoped case: we hand-roll a media-scoped <link rel="preload">, so the
  //   <img> only needs to be eager. next/image's `priority` would emit its own
  //   un-scoped preload and defeat the scoping, so we set loading/fetchPriority
  //   directly instead.
  // - Otherwise: `priority` handles eager + fetchpriority + preload in one.
  const eagerImageProps: Partial<React.ComponentProps<typeof RetryingImage>> =
    !prerender
      ? {}
      : useScopedPreload
        ? { loading: 'eager', fetchPriority: 'high' }
        : { priority: true };

  // Queue the media-scoped <head> preloads during render (see
  // preloadScopedImage). Kept out of JSX so React emits them as hoisted,
  // media-preserving resources instead of in-body <link> elements.
  if (useScopedPreload && hasMobileVariant) {
    preloadScopedImage({
      media: MOBILE_MEDIA,
      src: getImageUrl(mobileImage),
      width: mobileImageSize.width,
      height: mobileImageSize.height,
      quality,
      sizes,
    });
    preloadScopedImage({
      media: DESKTOP_MEDIA,
      src: getImageUrl(image),
      width: imageSize.width,
      height: imageSize.height,
      quality,
      sizes,
    });
  } else if (useScopedPreload && preloadMedia) {
    preloadScopedImage({
      media: preloadMedia,
      src: getImageUrl(image),
      width: imageSize.width,
      height: imageSize.height,
      quality,
      sizes,
    });
  }

  return (
    <figure
      data-component-id="C007"
      className={clsx(
        'smzh-image flex flex-col gap-2 object-center',
        className,
      )}
    >
      {mobileImage?.url && (
        <RetryingImage
          className={`${imgClasses} block lg:hidden`}
          src={getImageUrl(mobileImage)}
          width={mobileImageSize.width}
          height={mobileImageSize.height}
          style={mobileImageSize.style}
          alt={imageAlt}
          aria-label={ariaLabel}
          quality={quality}
          sizes={sizes}
          {...eagerImageProps}
        />
      )}
      <RetryingImage
        className={clsx(imgClasses, { 'hidden lg:block': mobileImage?.url })}
        src={getImageUrl(image)}
        width={imageSize.width}
        height={imageSize.height}
        style={imageSize.style}
        alt={imageAlt}
        aria-label={ariaLabel}
        quality={quality}
        sizes={sizes}
        {...eagerImageProps}
      />
      {caption && (
        <figcaption className="smzh-fig-caption w-full text-center text-sm font-normal text-neutral-500">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
